import { ExecutionItem, ExecutionStatus } from './ManageExecution.interface';
import { logger } from 'src/utils/logger';
import { getDateAndTimeFromEpoch } from 'src/utils/CommonUtils';

export const MAX_RANK = 999999;
export const MAX_RANK_DISPLAY = 'MAX_RANK';

export const STATUS_TRANSITIONS: Record<ExecutionStatus, ExecutionStatus[]> = {
	RUNNING: ['PAUSED', 'CANCELLED'],
	PAUSED: ['RUNNING', 'CANCELLED'],
	CANCELLED: ['RUNNING'],
};

const STATUS_COLOR_MAP: Record<string, 'green' | 'blue' | 'red'> = {
	RUNNING: 'green',
	PAUSED: 'blue',
	CANCELLED: 'red',
};

const DEFAULT_STATUS_COLOR = 'blue';
const EPOCH_MILLIS_THRESHOLD = 1e12;
const MS_PER_SECOND = 1000;

export function getIndicatorType(status: string): 'success' | 'error' | 'in-progress' {
	const colorType = STATUS_COLOR_MAP[status] || DEFAULT_STATUS_COLOR;

	if (colorType === 'green') return 'success';
	if (colorType === 'red') return 'error';

	return 'in-progress';
}

export function formatRank(rank: number | unknown): string {
	const num = Number(rank);
	if (isNaN(num)) return 'N/A';
	return num === MAX_RANK ? MAX_RANK_DISPLAY : String(num);
}

export function formatMergeStatus(blocked: number): string {
	return blocked === 0 ? 'Not Blocked' : 'Blocked';
}

export function formatDateTime(dateStr: string | undefined | null): string {
	if (!dateStr) return '-';
	try {
		const date = new Date(dateStr);

		if (isNaN(date.getTime())) return dateStr;

		return date.toUTCString();
	} catch (err) {
		logger.warn('formatDateTime: Failed to parse date', { dateStr, err });
		return dateStr;
	}
}

function toISOString(value: unknown): string {
	if (!value) return '';
	const num = Number(value);
	if (isNaN(num) || num <= 0) return String(value);
	const ms = num < EPOCH_MILLIS_THRESHOLD ? num * MS_PER_SECOND : num;
	return new Date(ms).toISOString();
}

export function transformExecutionData(exec: Record<string, unknown>, workflowName?: string): ExecutionItem {
	const entities = Array.isArray(exec.entities) ? exec.entities as string[] : [];
	const status = ((exec.status || '') as string).toUpperCase();

	return {
		cycleId: (exec.workflowExecutionId || '') as string,
		workflowType: (exec.workflowId || '') as string,
		workflowName: workflowName || (exec.workflowName || '') as string,
		entities,
		runningTaskRank: 0,
		blocked: 0,
		status: status as ExecutionStatus,
		owner: (exec.startedBy || '') as string,
		creationTime: toISOString(exec.startTime),
		actualCompletionTime: toISOString(exec.completionTime),
		lastStatusUpdateTime: toISOString(exec.lastUpdateTime),
		lastStatusUpdatedBy: (exec.lastUpdatedBy || '') as string,
		expectedCompletionTime: '',
		workflowOrchestrator: (exec.workflowOrchestrator || '') as string,
		entityType: (exec.entityType || '') as string,
	};
}



export interface ExecutionRawEntity {
	CycleId?: string;
	Status?: string;
	CreationTime?: string;
	ActualCompletionTime?: string;
	ActualStartTime?: string;
	LastStatusUpdateTime?: string;
	LastStatusUpdatedBy?: string;
	EntityData?: Record<string, unknown>;
	EntityAttributes?: Record<string, unknown>;
	[key: string]: unknown;
}

export function extractField(raw: ExecutionRawEntity, field: string): unknown {
	return raw[field] ?? raw.EntityData?.[field] ?? raw.EntityAttributes?.[field];
}

export function formatEpochOrISO(timestamp: unknown): string {
	if (!timestamp) return '';
	const str = String(timestamp);
	const num = Number(str);
	if (!isNaN(num) && num > 0) {
		const epochSeconds = num > EPOCH_MILLIS_THRESHOLD ? Math.floor(num / MS_PER_SECOND) : num;
		return getDateAndTimeFromEpoch(epochSeconds) || '';
	}
	try {
		const date = new Date(str);
		if (!isNaN(date.getTime())) return date.toUTCString();
	} catch (err) { logger.warn('formatEpochOrISO: Failed to parse date', { str, err }); }
	return str;
}
export function extractEntities(raw: Record<string, unknown>, entityData: Record<string, unknown>): string[] {
	if (Array.isArray(raw.Entities)) return raw.Entities as string[];
	if (typeof entityData.Entities === 'string') {
		return (entityData.Entities as string).split(',').map((entityName: string) => entityName.trim());
	}
	if (Array.isArray(entityData.Entities)) return entityData.Entities as string[];
	if (raw.EntityName) return [raw.EntityName as string];
	return [];
}

interface FilterToken {
	propertyKey?: string;
	operator: string;
	value: string;
}

const EQUALITY_OPERATORS = /^(=|!=)$/;
const COMPARISON_OPERATORS = /^(>|<|>=|<=)$/;

function matchContains(haystack: string, needle: string, negate: boolean): boolean {
	const result = haystack.includes(needle);
	return negate ? !result : result;
}

function matchEquality(actual: string, expected: string, negate: boolean): boolean {
	const result = actual === expected;
	return negate ? !result : result;
}

function matchComparison(actual: number, expected: number, operator: string): boolean {
	switch (operator) {
		case '>': return actual > expected;
		case '<': return actual < expected;
		case '>=': return actual >= expected;
		case '<=': return actual <= expected;
		default: return true;
	}
}

function matchExecutionToken(item: ExecutionItem, token: FilterToken): boolean {
	const { propertyKey, operator, value } = token;
	const lowerValue = (value || '').toLowerCase();

	if (propertyKey === 'entities') {
		const entitiesStr = item.entities.join(',').toLowerCase();
		if (/^(!?:)$/.test(operator)) return matchContains(entitiesStr, lowerValue, operator === '!:');
		if (EQUALITY_OPERATORS.test(operator)) {
			const hasMatch = item.entities.some(entity => entity.toLowerCase() === lowerValue);
			return operator === '!=' ? !hasMatch : hasMatch;
		}
	}

	if (propertyKey === 'status') {
		return matchEquality(item.status.toLowerCase(), lowerValue, operator === '!=');
	}

	if (propertyKey === 'mergeStatus') {
		return matchEquality(formatMergeStatus(item.blocked).toLowerCase(), lowerValue, operator === '!=');
	}

	if (propertyKey === 'currentRank') {
		const rankStr = formatRank(item.runningTaskRank);
		if (EQUALITY_OPERATORS.test(operator)) return matchEquality(rankStr, value, operator === '!=');
		const numVal = Number(value);
		if (!isNaN(numVal) && COMPARISON_OPERATORS.test(operator)) {
			return matchComparison(item.runningTaskRank, numVal, operator);
		}
	}

	if (propertyKey === 'creationYear') {
		if (!item.creationTime) return operator === '!=';
		try {
			const year = new Date(item.creationTime).getFullYear().toString();
			return matchEquality(year, value, operator === '!=');
		} catch {
			return operator === '!=';
		}
	}

	if (propertyKey === 'creationMonth') {
		if (!item.creationTime) return operator === '!=';
		try {
			const date = new Date(item.creationTime);
			const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
			const month = monthNames[date.getMonth()];
			return matchEquality(month.toLowerCase(), lowerValue, operator === '!=');
		} catch {
			return operator === '!=';
		}
	}

	if (!propertyKey) {
		const searchable = [
			item.entities.join(','),
			item.status,
			formatMergeStatus(item.blocked),
			formatRank(item.runningTaskRank),
			item.cycleId,
		].join(' ').toLowerCase();
		return searchable.includes(lowerValue);
	}

	return true;
}

export function filterExecutions(item: ExecutionItem, tokens: readonly FilterToken[], operation: string): boolean {
	if (operation === 'and') {
		return tokens.every(token => matchExecutionToken(item, token));
	}
	return tokens.some(token => matchExecutionToken(item, token));
}
